'use client'

import { useCallback, useEffect } from 'react'

import { URLS } from '@/configs/urls'
import { useRouter } from '@/i18n/routing'

const LIST_URL_KEY = 'arena:tasksListUrl'
const SCROLL_STATE_KEY = 'tasksListScrollY'
const SCROLL_RESTORE_TIMEOUT_MS = 3000

// Per document: a task opened in a new tab or after a reload has no list
// behind it in this history, so it must not go back.
let taskOpenedFromList: string | null = null

/** The list calls it right before it opens a task. */
export function rememberTasksList(taskHref: string): void {
  taskOpenedFromList = new URL(taskHref, window.location.href).pathname
  // Kept on the list's own history entry: only a return to that entry (Back,
  // browser back) restores it, a fresh visit to the list is a new entry.
  window.history.replaceState(
    { ...window.history.state, [SCROLL_STATE_KEY]: window.scrollY },
    ''
  )
  try {
    sessionStorage.setItem(
      LIST_URL_KEY,
      window.location.pathname + window.location.search
    )
  } catch {
    // Storage blocked: Back still works within this document.
  }
}

function rememberedListUrl(): string {
  try {
    return sessionStorage.getItem(LIST_URL_KEY) ?? URLS.TASKS
  } catch {
    return URLS.TASKS
  }
}

/**
 * Back from a task. Opened from the list: a history step back, which brings
 * the list URL (filters, sort) and its scroll position. Otherwise: the last
 * list the fan saw, or the bare list.
 */
export function useBackToTasksList(): () => void {
  const router = useRouter()
  return useCallback(() => {
    if (taskOpenedFromList === window.location.pathname) {
      router.back()
      return
    }
    router.push(rememberedListUrl())
  }, [router])
}

/**
 * The list scrolls back to where the fan opened a task. The browser's own
 * restoration runs before the feeds render, so wait for the page to grow.
 */
export function useRestoreTasksListScroll(): void {
  useEffect(() => {
    const y: unknown = window.history.state?.[SCROLL_STATE_KEY]
    if (typeof y !== 'number' || y <= 0) {
      return
    }
    const restore = () => {
      if (document.documentElement.scrollHeight - window.innerHeight < y) {
        return false
      }
      window.scrollTo(0, y)
      return true
    }
    if (restore()) {
      return
    }
    const observer = new ResizeObserver(() => {
      if (restore()) {
        observer.disconnect()
      }
    })
    observer.observe(document.body)
    const timeout = window.setTimeout(() => {
      observer.disconnect()
    }, SCROLL_RESTORE_TIMEOUT_MS)
    return () => {
      observer.disconnect()
      window.clearTimeout(timeout)
    }
  }, [])
}
